import { useAlert } from '@/common/alert/AlertContext';
import { ApprRouteProgressButton } from '@/common/buttons/ApprRouteProgressButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { EntityPropList } from '@/common/entities/entityProp/EntityPropList';
import { EntityDetail } from '@/common/entityFramework/entityDetail/EntityDetail';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ValList } from '@/common/val/ValList';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useState } from 'react';
import { CtrtDistList } from '../ctrtDist/CtrtDistList';
import { CtrtRelList } from '../ctrtRel/CtrtRelList';
import { McdStateList } from '../mcdState/McdStateList';
import { ProgList } from '../prog/ProgList';
import { CtrtAmendmentList } from './CtrtAmendmentList';
import { CtrtForm } from './CtrtForm';
import { useCtrtOutline } from './useCtrtOutline';

export const CtrtDetail = () => {
  const { entityObj } = useEntityMgr();
  const { navigateTo } = useNavState();
  const { showAlert } = useAlert();
  const rsrc = useRsrc();

  // No focus entity here, so the Contract's own outline opens at its default depth
  const { augButtons: augOutlineButton, outlineContent } = useCtrtOutline({ ctrtId: entityObj?.id });

  // An implemented Contract has no apprJobId of its own; the approval lives on the amendment that was implemented
  const [amendmentApprJobId, setAmendmentApprJobId] = useState();
  useEffect(() => {
    if (entityObj?.status !== 'IMPL' || !entityObj?.code) {
      setAmendmentApprJobId(null);
      return;
    }

    const rowsReq = { query: 'LATEST_AMENDMENT_BY_CODE', replaceParams: { code: entityObj.code } };
    rsrc.one('md', 'CTRT', rowsReq).then((amendment) => setAmendmentApprJobId(amendment?.apprJobId));
  }, [entityObj?.id, entityObj?.status, entityObj?.code]);

  const compareWithOriginal = async () => {
    if (!entityObj?.id || !entityObj?.code) {
      return;
    }
    const baseCode = entityObj.code.replace(/_\d{3}$/, '');
    const expr = ExprUtils.and(ExprUtils.whereParams('code', baseCode), ExprUtils.whereParams('status', 'IMPL'));
    const originalCtrt = await rsrc.one('md', 'CTRT', { expr });
    if (!originalCtrt?.id) {
      showAlert('Original implemented contract was not found for this amendment.', 'warning');
      return;
    }
    if (originalCtrt.id === entityObj.id) {
      showAlert('This contract is already the original implemented contract.', 'warning');
      return;
    }
    navigateTo('md/compare/ctrt', { ctrtIds: [entityObj.id, originalCtrt.id] });
  };

  const augButtons = (buttons) => {
    augOutlineButton(buttons);

    if (amendmentApprJobId) {
      buttons.push(<ApprRouteProgressButton key="apprRouteProgressButton" apprJobId={amendmentApprJobId} />);
    }

    if (entityObj?.status === 'IMPL') {
      return;
    }

    buttons.push(
      <TitleButton
        key="compareWithOriginalButton"
        icon={fas.faBalanceScale}
        toolTip="Compare amendment with original contract"
        onClick={compareWithOriginal}
      />,
    );
  };

  const tabSpecs = [
    { id: 'infoTab', label: 'Info', content: <CtrtForm /> },
    { id: 'prog', label: 'Programs', content: <ProgList key="progList" ctrtObj={entityObj} /> },
  ];

  if (entityObj.typeCode === 'MCD') {
    tabSpecs.push({ id: 'state', label: 'States', content: <McdStateList ctrtObj={entityObj} /> });
  }

  if (entityObj.typeCode === 'GPO') {
    tabSpecs.push({ id: 'dist', label: 'Distributors', content: <CtrtDistList ctrtObj={entityObj} /> });
  }

  tabSpecs.push(
    {
      id: 'entityProps',
      label: 'Properties',
      content: (
        <EntityPropList parentEntity="CTRT" parentEntityObj={entityObj} showAddButton={entityObj.status !== 'IMPL'} />
      ),
    },
    { id: 'ctrtRel', label: 'Relationships', content: <CtrtRelList ctrtId={entityObj.id} /> },
    { id: 'val', label: 'Validations', content: <ValList parentEntity="CTRT" parentEntityId={entityObj.id} /> },
  );

  if (entityObj.status === 'IMPL') {
    tabSpecs.push({ id: 'amendment', label: 'Amendments', content: <CtrtAmendmentList ctrtObj={entityObj} /> });
  }

  return (
    <EntityDetail entity="CTRT" tabSpecs={tabSpecs} augButtons={augButtons}>
      {outlineContent}
    </EntityDetail>
  );
};
